import axios from 'axios';
import head from 'lodash/head';
import { ANALYTICS_CART_ID, ANALYTICS_ORDER_ID, NA_VALUE } from '@yme/common/constants';
import { TRANSACTION_DETAILS } from '@yme/common/endpoints';
import { ADD_PRODUCT_TO_CART_FAILURE, ADD_PRODUCT_TO_CART_REQUEST, ADD_PRODUCT_TO_CART_SUCCESS, CHANGE_ITEM_QUANTITY_REQUEST, CHANGE_ITEM_QUANTITY_FAILURE, CHANGE_ITEM_QUANTITY_SUCCESS, GET_CART_SUCCESS, CHANGE_SHIPPING_METHOD_SUCCESS, } from '@yme/common/redux/cart/actionTypes';
import { CART_ID_SEPARATOR } from '@yme/common/redux/cart/constants';
import { changeLanguageList } from '@yme/common/redux/common/reducer';
import { SET_GOOGLE_SEARCHED_PLACE } from '@yme/components/CheckoutForm/ShippingDetailsForm/PreferredDealer/GoogleLocationSearch/redux/actions';
import { CHECKOUT_PAGE_ORDER, CheckoutSteps } from '@yme/components/CheckoutForm/constants';
import { SET_ACTIVE_PREFER_DEALER, SET_AS_A_GUEST_EMAIL, SET_CURRENT_STEP_NUMBER, } from '@yme/components/CheckoutForm/redux/actionTypes';
import { REMOVE_COMPARISON_PRODUCT, ADD_COMPARISON_PRODUCT, } from '@yme/components/Comparison/redux/actionTypes';
import { SET_DEALER_LOCATOR_SEARCHED_PLACE } from '@yme/components/DealerLocator/redux/actionTypes';
import { SET_IS_AUTHORIZED, SET_PROFILE_DATA, } from '@yme/components/Header/ProfileMenu/redux/actions';
import { GET_NEWS_EVENTS_ARTICLES_SUCCESS } from '@yme/components/NewsPage/components/NewsEventsGrid/redux/actions';
import { SET_CHECK_COMPATIBILITY_PRODUCT, SET_FILTER_OPTIONS, SET_IS_NOTIFY_ME_LOADED, SET_VIEW_360_STATE, } from '@yme/components/ProductDetailsPage/components/ProductCard/redux/actions';
import { SET_ACTIVE_COUNTRY_OPTION } from '@yme/molecules/language-selector/redux/actionTypes';
import { NEWS_TYPE, PARAMS_KEYS } from '@yme/organisms/news-events-grid/constants';
import { MOTORCYCLE_LOWERED_VERSION_KEY, MOTORCYCLE_POWER_VERSION_KEY, MOTORCYCLE_TRANSMISSION_KEY, } from '@yme/selectors/constants';
import { setPixelPageView, PixelPageView, getConversionData } from '@yme/utils/bloomreach';
import { setDigitalData, DigitalDataMutation, isInsideClickEventFromProductOptions, getAttributeByName, } from '@yme/utils/common';
import { METHOD_GET, METHOD_POST } from '@yme/utils/constants';
import { getSelectedMarketingChannelOptions } from '@yme/utils/privacyConsent';
import { getMasterVariant, getPdpLinkByCategory } from '@yme/utils/product';
function defaultAddToCartState() {
    return {
        sku: '',
        quantity: 0,
        entry: null,
        prevQuantity: 0,
    };
}
function defaultFilterOptions() {
    return {
        size: '',
        color: '',
        mutationIndex: 0,
        transmission: '',
        power: '',
        loweredVersion: '',
        //scooters & boats
        packageVersion: '',
        //engines
        rotation: '',
        startingMethod: '',
        shaftLength: '',
        controlMethod: '',
    };
}
function getEnVersionOfMotorcycleAttribute({ product, language, attribute, localizedAttributeValue, }) {
    if (!product) {
        return '';
    }
    const groupingProducts = product?.groupingProducts?.length ? product?.groupingProducts : [];
    const groupingProductsAttributeArr = groupingProducts.reduce((acc, item) => {
        const masterVariant = getMasterVariant(item?.variants);
        const productAttributeValue = masterVariant
            ? getAttributeByName(masterVariant?.attributes, attribute)
            : {};
        return [...acc, productAttributeValue];
    }, []);
    const mainProductMasterVariant = getMasterVariant(product?.variants);
    const mainProductAttributeValue = mainProductMasterVariant
        ? getAttributeByName(mainProductMasterVariant?.attributes, attribute)
        : {};
    const motorcycleAttributeArr = [...groupingProductsAttributeArr, mainProductAttributeValue];
    return (motorcycleAttributeArr.find((item) => item[language] === localizedAttributeValue)?.en || '');
}
const sharedIntermediateState = {
    [SET_CURRENT_STEP_NUMBER]: CHECKOUT_PAGE_ORDER.shoppingCart,
    [ADD_PRODUCT_TO_CART_REQUEST]: defaultAddToCartState(),
    [CHANGE_ITEM_QUANTITY_REQUEST]: defaultAddToCartState(),
    [SET_FILTER_OPTIONS]: defaultFilterOptions(),
};
class AwaitedAction {
    promise;
    resolve;
    constructor() {
        this.promise = new Promise((resolve) => {
            this.resolve = resolve;
        });
    }
}
const AwaitedActions = {
    [GET_CART_SUCCESS]: new AwaitedAction(),
};
/*
  Since our store filled asynchronously, we need to wait for some actions to be dispatched
  before we can get needed data from store.
*/
function waitForActions(names, callback) {
    const promises = names.map((name) => AwaitedActions[name].promise);
    // we need timeout here to perform all needed mutations in store
    Promise.all(promises).then(() => setTimeout(callback, 0));
}
export const analyticsDataMiddleware = (api) => (next) => (action) => {
    if (action.type in AwaitedActions) {
        AwaitedActions[action.type].resolve();
    }
    switch (action.type) {
        case SET_CHECK_COMPATIBILITY_PRODUCT: {
            const payload = action.payload;
            if (payload?.product && payload.isUserAction) {
                const productId = payload.product.slug;
                if (payload.isSuccessCheck) {
                    setDigitalData(DigitalDataMutation.COMPATIBILITY_RESULT_POSITIVE, {
                        productId,
                    });
                }
                else {
                    setDigitalData(DigitalDataMutation.COMPATIBILITY_RESULT_NEGATIVE, {
                        productId,
                    });
                }
            }
            break;
        }
        case CHANGE_SHIPPING_METHOD_SUCCESS: {
            const payload = action.payload;
            const isShoppingDetailsPage = sharedIntermediateState[SET_CURRENT_STEP_NUMBER] === CHECKOUT_PAGE_ORDER.shippingDetails;
            const isShoppingDetailsUrl = window.location.hash.includes(`#${CheckoutSteps.SHIPPING_DETAILS}`);
            waitForActions([GET_CART_SUCCESS], () => {
                const { cart, common } = api.getState();
                if (!cart.data || !common.currencyRate) {
                    return;
                }
                if (isShoppingDetailsPage && isShoppingDetailsUrl) {
                    setDigitalData(DigitalDataMutation.CHECKOUT_CHANGE_SHIPPING_METHOD, {
                        method: payload.shippingInfo?.name,
                        cart: cart.data,
                        getPdpLinkByCategory: getPdpLinkByCategory.bind(null, common.pdpLinks),
                    }, common.currencyRate);
                }
                else {
                    setDigitalData(DigitalDataMutation.CHANGE_SHIPPING_METHOD, {
                        method: payload.shippingInfo?.name,
                        cart: cart.data,
                        getPdpLinkByCategory: getPdpLinkByCategory.bind(null, common.pdpLinks),
                    }, common.currencyRate);
                }
            });
            break;
        }
        case SET_IS_AUTHORIZED: {
            const isAuthorized = action.payload;
            if (!isAuthorized) {
                setDigitalData(DigitalDataMutation.LOGOUT, null);
            }
            break;
        }
        case SET_PROFILE_DATA: {
            const { common: { pdpLinks, currencyRate }, checkout: { isCheckoutLogin }, cart, } = api.getState();
            const payload = action.payload;
            const userId = payload.customAttributes?.myYamahaUserId ?? null;
            if (userId) {
                setDigitalData(DigitalDataMutation.SET_USER, {
                    profile: {
                        profileInfo: {
                            userName: userId,
                        },
                    },
                });
            }
            if (userId && isCheckoutLogin && cart.data && currencyRate) {
                setDigitalData(DigitalDataMutation.CHECKOUT_LOGIN, {
                    userName: userId,
                    cart: cart.data,
                    getPdpLinkByCategory: getPdpLinkByCategory.bind(null, pdpLinks),
                }, currencyRate);
            }
            break;
        }
        case SET_AS_A_GUEST_EMAIL: {
            const { common: { pdpLinks, currencyRate }, cart, } = api.getState();
            if (cart.data && currencyRate) {
                setDigitalData(DigitalDataMutation.CHECKOUT_LOGIN, {
                    userName: null,
                    cart: cart.data,
                    getPdpLinkByCategory: getPdpLinkByCategory.bind(null, pdpLinks),
                }, currencyRate);
            }
            break;
        }
        case GET_CART_SUCCESS: {
            sharedIntermediateState[SET_FILTER_OPTIONS].mutationIndex = 0;
            break;
        }
        case SET_VIEW_360_STATE: {
            const isOpened = action.payload;
            if (isOpened) {
                setDigitalData(DigitalDataMutation.PDP_360, null);
            }
            break;
        }
        case SET_IS_NOTIFY_ME_LOADED: {
            const isInProgress = action.payload;
            const { productCard, privacyConsent } = api.getState();
            const { sku } = productCard.filterOptions;
            if (!isInProgress && sku) {
                setDigitalData(DigitalDataMutation.NOTIFY_ME_CONFIRMATION, {
                    marketingChannel: getSelectedMarketingChannelOptions(privacyConsent),
                    sku,
                });
            }
            break;
        }
        case SET_FILTER_OPTIONS: {
            const { productCard: { filterOptions }, productDetailsPage: { data }, common: { language }, } = api.getState();
            if (!isInsideClickEventFromProductOptions()) {
                break;
            }
            const payload = action.payload;
            if (payload?.sku) {
                if (payload?.size &&
                    filterOptions.size !== payload?.size &&
                    payload?.size.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_CHANGE_SIZE, {
                        size: payload.size,
                        sku: payload.sku,
                    });
                }
                if (payload?.colourId &&
                    filterOptions.colourId !== payload?.colourId &&
                    payload?.colourId.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_CHANGE_COLOR, {
                        color: payload.colourId,
                        sku: payload.sku,
                    });
                }
                if (payload?.power &&
                    filterOptions.power !== payload?.power &&
                    payload?.power.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        powerVersion: getEnVersionOfMotorcycleAttribute({
                            product: data,
                            attribute: MOTORCYCLE_POWER_VERSION_KEY,
                            language,
                            localizedAttributeValue: payload.power,
                        }),
                        sku: payload.sku,
                    });
                }
                if (payload?.transmission &&
                    filterOptions.transmission !== payload?.transmission &&
                    payload?.transmission.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        transmission: getEnVersionOfMotorcycleAttribute({
                            product: data,
                            attribute: MOTORCYCLE_TRANSMISSION_KEY,
                            language,
                            localizedAttributeValue: payload.transmission,
                        }),
                        sku: payload.sku,
                    });
                }
                if (payload?.loweredVersion &&
                    filterOptions.loweredVersion !== payload?.loweredVersion &&
                    payload?.loweredVersion.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        loweredVersion: getEnVersionOfMotorcycleAttribute({
                            product: data,
                            attribute: MOTORCYCLE_LOWERED_VERSION_KEY,
                            language,
                            localizedAttributeValue: payload.loweredVersion,
                        }),
                        sku: payload.sku,
                    });
                }
                if (payload?.rotation &&
                    filterOptions.rotation !== payload?.rotation &&
                    payload?.rotation.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        rotation: payload.rotation,
                        sku: payload.sku,
                    });
                }
                if (payload?.startingMethod &&
                    filterOptions.startingMethod !== payload?.startingMethod &&
                    payload?.startingMethod.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        startingMethod: payload.startingMethod,
                        sku: payload.sku,
                    });
                }
                if (payload?.shaftLength &&
                    filterOptions.shaftLength !== payload?.shaftLength &&
                    payload?.shaftLength.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        shaftLength: payload.shaftLength,
                        sku: payload.sku,
                    });
                }
                if (payload?.controlMethod &&
                    filterOptions.controlMethod !== payload?.controlMethod &&
                    payload?.controlMethod.toUpperCase() !== NA_VALUE) {
                    setDigitalData(DigitalDataMutation.PDP_SKU_CHANGE, {
                        controlMethod: payload.controlMethod,
                        sku: payload.sku,
                    });
                }
            }
            break;
        }
        case SET_GOOGLE_SEARCHED_PLACE: {
            const { value } = action.payload;
            if (typeof value === 'string' && value.length) {
                setDigitalData(DigitalDataMutation.CHECKOUT_DEALER_SEARCH, {
                    location: value ?? '',
                });
            }
            break;
        }
        case SET_ACTIVE_PREFER_DEALER: {
            const { vicinityId } = action.payload;
            const { checkout } = api.getState();
            const dealer = checkout.dealerData.dealerList.find((el) => el.vicinityId === vicinityId);
            if (dealer) {
                setDigitalData(DigitalDataMutation.CHECKOUT_DEALER_SELECT, {
                    location: dealer.city,
                    dealerName: dealer.name,
                    dealerId: dealer.vicinityId,
                });
            }
            break;
        }
        case SET_CURRENT_STEP_NUMBER: {
            sharedIntermediateState[SET_CURRENT_STEP_NUMBER] = action.payload;
            if (action.payload === CHECKOUT_PAGE_ORDER.successPage) {
                const { cart, common, checkout, checkoutPrivacyConsent } = api.getState();
                const orderId = localStorage.getItem(ANALYTICS_ORDER_ID);
                const cartId = localStorage.getItem(ANALYTICS_CART_ID);
                if (!cartId || !orderId) {
                    break;
                }
                const apiUrlCart = cart.hyperdriveEndpoints.getCartById;
                const axiosCartConfig = {
                    method: METHOD_GET,
                    url: apiUrlCart.replace(CART_ID_SEPARATOR, cartId),
                };
                const axiosTransactionConfig = {
                    method: METHOD_POST,
                    url: TRANSACTION_DETAILS,
                    data: {
                        isoCountryCode: common.isoCountryCode,
                    },
                };
                axios
                    .request(axiosCartConfig)
                    .then((result) => {
                    if (!result.data) {
                        return;
                    }
                    let paymentMethod = '';
                    if (checkout.paymentProvider === 'adyen') {
                        axios
                            .request(axiosTransactionConfig)
                            .then((transactionResult) => {
                            paymentMethod = transactionResult.data;
                        })
                            .finally(() => {
                            if (common.currencyRate) {
                                setDigitalData(DigitalDataMutation.CHECKOUT_CONFIRMATION, {
                                    cart: result.data,
                                    orderId,
                                    marketingChannel: getSelectedMarketingChannelOptions(checkoutPrivacyConsent),
                                    paymentMethod,
                                    getPdpLinkByCategory: getPdpLinkByCategory.bind(null, common.pdpLinks),
                                }, common.currencyRate);
                            }
                            if (!common.isInAuthor) {
                                setPixelPageView(PixelPageView.CONVERSION, {
                                    ...getConversionData(result.data, orderId),
                                    domain_key: common.bloomreachModel.productCatalog,
                                });
                            }
                        });
                    }
                    else {
                        if (checkout.paymentProvider === 'cyclescheme') {
                            paymentMethod = 'CERTIFICATE';
                        }
                        else if (checkout.paymentProvider === 'payever') {
                            paymentMethod = 'payever';
                        }
                        if (common.currencyRate) {
                            setDigitalData(DigitalDataMutation.CHECKOUT_CONFIRMATION, {
                                cart: result.data,
                                orderId,
                                marketingChannel: getSelectedMarketingChannelOptions(checkoutPrivacyConsent),
                                paymentMethod,
                                getPdpLinkByCategory: getPdpLinkByCategory.bind(null, common.pdpLinks),
                            }, common.currencyRate);
                        }
                        if (!common.isInAuthor) {
                            setPixelPageView(PixelPageView.CONVERSION, {
                                ...getConversionData(result.data, orderId),
                                domain_key: common.bloomreachModel.productCatalog,
                            });
                        }
                    }
                })
                    .finally(() => {
                    localStorage.removeItem(ANALYTICS_ORDER_ID);
                    localStorage.removeItem(ANALYTICS_CART_ID);
                });
            }
            else {
                waitForActions([GET_CART_SUCCESS], () => {
                    const { cart, common } = api.getState();
                    if (!cart.data) {
                        return;
                    }
                    let mutationName = null;
                    if (action.payload === CHECKOUT_PAGE_ORDER.shippingDetails) {
                        mutationName = DigitalDataMutation.CHECKOUT_SHIPPING_DETAILS;
                    }
                    else if (action.payload === CHECKOUT_PAGE_ORDER.orderReview) {
                        mutationName = DigitalDataMutation.CHECKOUT_REVIEW;
                    }
                    else if (action.payload === CHECKOUT_PAGE_ORDER.payAndPlaceOrder) {
                        mutationName = DigitalDataMutation.CHECKOUT_PAYMENT_DETAILS;
                    }
                    if (!mutationName || !common.currencyRate) {
                        return;
                    }
                    setDigitalData(mutationName, {
                        cart: cart.data,
                        getPdpLinkByCategory: getPdpLinkByCategory.bind(null, common.pdpLinks),
                    }, common.currencyRate);
                });
            }
            break;
        }
        case SET_DEALER_LOCATOR_SEARCHED_PLACE: {
            const payload = action.payload;
            if (payload.value) {
                setDigitalData(DigitalDataMutation.DEALER_LOCATOR_SELECT_LOCATION, payload.value);
            }
            break;
        }
        case CHANGE_ITEM_QUANTITY_REQUEST: {
            const payload = action.payload;
            const cart = api.getState().cart.data ?? {
                entries: [],
            };
            const target = cart.entries.find((item) => item.product.variants.some((v) => v.sku === payload.sku));
            sharedIntermediateState[CHANGE_ITEM_QUANTITY_REQUEST] = {
                ...payload,
                entry: target ?? null,
                prevQuantity: target?.quantity ?? 0,
            };
            break;
        }
        case CHANGE_ITEM_QUANTITY_FAILURE:
            sharedIntermediateState[CHANGE_ITEM_QUANTITY_REQUEST] = defaultAddToCartState();
            break;
        case CHANGE_ITEM_QUANTITY_SUCCESS:
            {
                const cart = action.payload;
                const { common: { currencyRate, pdpLinks }, } = api.getState();
                const target = sharedIntermediateState[CHANGE_ITEM_QUANTITY_REQUEST];
                sharedIntermediateState[CHANGE_ITEM_QUANTITY_REQUEST] = defaultAddToCartState();
                const isAddition = target.quantity > target.prevQuantity;
                const diff = Math.abs(target.quantity - target.prevQuantity);
                //Just technical event. Will be ignored in adobe analytic
                if (!isAddition && currencyRate) {
                    setDigitalData(DigitalDataMutation.CLEAR_LINE_ITEMS, {
                        cart,
                        getPdpLinkByCategory: getPdpLinkByCategory.bind(null, pdpLinks),
                    }, currencyRate);
                }
                if (currencyRate) {
                    setDigitalData(isAddition ? DigitalDataMutation.ADD_TO_CART : DigitalDataMutation.REMOVE_FROM_CART, {
                        cart,
                        sku: target.sku,
                        quantity: diff,
                        entry: target.entry,
                        getPdpLinkByCategory: getPdpLinkByCategory.bind(null, pdpLinks),
                    }, currencyRate);
                }
            }
            break;
        case ADD_PRODUCT_TO_CART_FAILURE:
            sharedIntermediateState[ADD_PRODUCT_TO_CART_REQUEST] = defaultAddToCartState();
            break;
        case ADD_PRODUCT_TO_CART_REQUEST:
            const payload = action.payload;
            const cart = api.getState().cart.data ?? {
                entries: [],
            };
            const target = cart.entries.find((item) => item.product.variants.some((v) => v.sku === payload.sku));
            sharedIntermediateState[ADD_PRODUCT_TO_CART_REQUEST] = {
                ...payload,
                entry: target ?? null,
                prevQuantity: target?.quantity ?? 0,
            };
            break;
        case ADD_PRODUCT_TO_CART_SUCCESS:
            {
                const { common: { currencyRate, pdpLinks }, faslet, } = api.getState();
                const { isFasletAnalyticTriggered } = faslet;
                const cart = action.payload;
                const target = sharedIntermediateState[ADD_PRODUCT_TO_CART_REQUEST];
                sharedIntermediateState[ADD_PRODUCT_TO_CART_REQUEST] = defaultAddToCartState();
                if (!isFasletAnalyticTriggered && currencyRate) {
                    setDigitalData(DigitalDataMutation.ADD_TO_CART, {
                        cart,
                        sku: target.sku,
                        entry: target.entry,
                        quantity: target.quantity,
                        getPdpLinkByCategory: getPdpLinkByCategory.bind(null, pdpLinks),
                    }, currencyRate);
                }
            }
            break;
        case REMOVE_COMPARISON_PRODUCT:
            {
                const { comparisonProducts, common } = api.getState();
                const { payload } = action;
                const removedProduct = comparisonProducts.products.find((product) => product.key === payload);
                const remainingProducts = comparisonProducts.products.filter((product) => product !== removedProduct);
                setDigitalData(DigitalDataMutation.RMV_CMP, {
                    products: remainingProducts,
                    id: removedProduct?.slug ?? '',
                    pdpPageUrl: getPdpLinkByCategory(common.pdpLinks, remainingProducts.length ? remainingProducts[0].categories : []),
                });
            }
            break;
        case ADD_COMPARISON_PRODUCT:
            {
                const { comparisonProducts, common } = api.getState();
                const { payload } = action;
                const product = payload;
                setDigitalData(DigitalDataMutation.ADD_CMP, {
                    products: [...comparisonProducts.products, product],
                    id: product?.slug ?? '',
                    pdpPageUrl: getPdpLinkByCategory(common.pdpLinks, product.categories),
                });
            }
            break;
        case GET_NEWS_EVENTS_ARTICLES_SUCCESS:
            const response = action.payload;
            const { newsEventsArticles } = api.getState();
            const searchParams = new URLSearchParams(window.location.search);
            const pageParam = searchParams.get(PARAMS_KEYS.page);
            const yearsParam = searchParams.get(PARAMS_KEYS.years);
            const categoriesParam = searchParams.get(PARAMS_KEYS.categories);
            const hasFilters = Boolean(yearsParam ?? categoriesParam);
            let mutationName = null;
            if (hasFilters) {
                mutationName =
                    newsEventsArticles.gridType === NEWS_TYPE
                        ? DigitalDataMutation.NEWS_LIST_FILTER
                        : DigitalDataMutation.EVENT_LIST_FILTER;
            }
            else {
                mutationName =
                    newsEventsArticles.gridType === NEWS_TYPE
                        ? DigitalDataMutation.NEWS_LIST_VIEW
                        : DigitalDataMutation.EVENT_LIST_VIEW;
            }
            setDigitalData(mutationName, {
                articleResponse: response.data,
                params: {
                    currentPage: Number(pageParam ?? 1),
                    years: yearsParam ? yearsParam.split(',') : [],
                    categories: categoriesParam ? categoriesParam.split(',') : [],
                },
            });
            break;
        case SET_ACTIVE_COUNTRY_OPTION:
            const { common: { countries }, } = api.getState();
            const activeCountry = countries.find((country) => country.code === action.payload);
            const languages = changeLanguageList(countries, action.payload);
            setDigitalData(DigitalDataMutation.NAVIGATION, {
                pageSection: 'country',
                navigationGroup: '',
                navigationItem: activeCountry
                    ? `${activeCountry.name}_(${head(languages)?.code ?? ''})`
                    : '',
            });
            break;
        default:
            break;
    }
    // Continue the middleware chain by calling the next middleware or reducer
    return next(action);
};
